<template>
  <div class="subjects-management">

    <!-- Header Section -->
    <div class="content-header row mb-2">
      <div class="content-header-left col-md-9 col-12 mb-2">
        <div class="row breadcrumbs-top">
          <div class="col-12">
            <h2 class="content-header-title float-left mb-0">
              <feather-icon icon="BookIcon" class="mr-1" size="20"/>
              Subjects Management
            </h2>
            <div class="breadcrumb-wrapper">
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <router-link to="/admin">Admin</router-link>
                </li>
                <li class="breadcrumb-item active">Subjects</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="row match-height mb-2">
      <div class="col-lg-3 col-md-6 col-12">
        <b-card no-body class="earnings-card">
          <b-card-body class="pb-0">
            <h6 class="text-muted font-weight-bolder text-uppercase mb-1">
              Total Subjects
            </h6>
            <h2 class="font-weight-bolder mb-1">{{ subjectStats.total }}</h2>
            <div class="d-flex justify-content-between">
              <feather-icon icon="BookIcon" size="24" class="text-primary"/>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div class="col-lg-3 col-md-6 col-12">
        <b-card no-body class="earnings-card">
          <b-card-body class="pb-0">
            <h6 class="text-muted font-weight-bolder text-uppercase mb-1">
              Active Subjects
            </h6>
            <h2 class="font-weight-bolder mb-1 text-success">{{ subjectStats.active }}</h2>
            <div class="d-flex justify-content-between">
              <feather-icon icon="CheckCircleIcon" size="24" class="text-success"/>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div class="col-lg-3 col-md-6 col-12">
        <b-card no-body class="earnings-card">
          <b-card-body class="pb-0">
            <h6 class="text-muted font-weight-bolder text-uppercase mb-1">
              With Icons
            </h6>
            <h2 class="font-weight-bolder mb-1 text-info">{{ subjectStats.withIcons }}</h2>
            <div class="d-flex justify-content-between">
              <feather-icon icon="ImageIcon" size="24" class="text-info"/>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div class="col-lg-3 col-md-6 col-12">
        <b-card no-body class="earnings-card">
          <b-card-body class="pb-0">
            <h6 class="text-muted font-weight-bolder text-uppercase mb-1">
              Programs
            </h6>
            <h2 class="font-weight-bolder mb-1 text-warning">{{ subjectStats.totalPrograms }}</h2>
            <div class="d-flex justify-content-between">
              <feather-icon icon="LayersIcon" size="24" class="text-warning"/>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>

    <!-- Quick Actions Card -->
    <div class="row mb-2">
      <div class="col-12">
        <b-card class="quick-actions-card">
          <b-card-body class="pb-0">
            <h5 class="mb-3">
              <feather-icon icon="ZapIcon" class="mr-1" size="18"/>
              Quick Actions
            </h5>
            <div class="row">
              <div class="col-md-8">
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                  <b-form
                      ref="form"
                      @submit.stop.prevent="handleSubmit(createOrUpdateSubject($event))"
                      class="form validate"
                  >
                    <div class="row">
                      <div class="col-md-4">
                        <validation-provider
                            v-slot="validationContext"
                            name="name"
                            :rules="{required: true, min: 2}"
                        >
                          <b-form-group
                              :label="$t('Subject Name')"
                              label-for="name"
                              :state="getValidationState(validationContext)"
                          >
                            <b-form-input
                                id="name"
                                type="text"
                                trim
                                v-model="form.name"
                                :state="getValidationState(validationContext)"
                                placeholder="Enter subject name..."
                            />
                            <b-form-invalid-feedback>
                              {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col-md-4">
                        <validation-provider
                            v-slot="validationContext"
                            name="description"
                            :rules="{required: true, min: 5}"
                        >
                          <b-form-group
                              :label="$t('Description')"
                              label-for="description"
                              :state="getValidationState(validationContext)"
                          >
                            <b-form-input
                                id="description"
                                type="text"
                                trim
                                v-model="form.description"
                                :state="getValidationState(validationContext)"
                                placeholder="Enter description..."
                            />
                            <b-form-invalid-feedback>
                              {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>

                      <div class="col-md-4">
                        <validation-provider
                            v-slot="validationContext"
                            name="icon"
                            :rules="form.id ? {} : {required: true}"
                        >
                          <b-form-group
                              :label="$t('Icon')"
                              label-for="icon"
                              :state="getValidationState(validationContext)"
                          >
                            <b-form-file
                                id="icon"
                                v-model="form.icon"
                                accept="image/*"
                                :state="getValidationState(validationContext)"
                                placeholder="Choose icon file..."
                                drop-placeholder="Drop icon here..."
                            />
                            <b-form-invalid-feedback>
                              {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </validation-provider>
                      </div>
                    </div>

                    <div class="d-flex justify-content-end">
                      <b-button
                        v-if="!form.id"
                        type="submit"
                        variant="primary"
                        class="mr-1"
                      >
                        <feather-icon icon="PlusIcon" size="16" class="mr-1"/>
                        {{ $t('Create Subject') }}
                      </b-button>
                      <template v-else>
                        <b-button
                          type="submit"
                          variant="primary"
                          class="mr-1"
                        >
                          <feather-icon icon="SaveIcon" size="16" class="mr-1"/>
                          {{ $t('Update Subject') }}
                        </b-button>
                        <b-button
                          @click="removeEditing"
                          variant="outline-secondary"
                        >
                          <feather-icon icon="XIcon" size="16" class="mr-1"/>
                          {{ $t('Cancel') }}
                        </b-button>
                      </template>
                    </div>
                  </b-form>
                </validation-observer>
              </div>
              <div class="col-md-4">
                <div class="text-center">
                  <feather-icon icon="BookOpenIcon" size="48" class="text-muted mb-2"/>
                  <h6 class="text-muted">Manage Subjects</h6>
                  <p class="text-muted small">Create and organize subjects for your educational programs</p>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </div>
    <!-- Enhanced Data Table -->
    <div class="row">
      <div class="col-12">
        <b-card>
          <div class="custom-data-bs-table">
            <!-- Table Header Controls -->
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div class="d-flex align-items-center">
                <label>Show</label>
                <b-form-select
                  v-model="pageLength"
                  :options="[5, 10, 15, 25]"
                  size="sm"
                  class="mx-1"
                  style="width: auto"
                />
                <label>entries</label>
              </div>

              <div class="d-flex align-items-center">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="getSubjects"
                  class="mr-1"
                >
                  <feather-icon icon="RefreshCwIcon" size="14"/>
                </b-button>
                <div class="d-flex align-items-center">
                  <label class="mr-1">Search:</label>
                  <b-form-input
                    v-model="searchTerm"
                    placeholder="Search subjects..."
                    size="sm"
                    style="min-width: 200px"
                  />
                </div>
              </div>
            </div>

            <!-- Enhanced Table -->
            <vue-good-table
              :columns="enhancedColumns"
              :rows="subjects"
              :search-options="{
                enabled: true,
                externalQuery: searchTerm
              }"
              :pagination-options="{
                enabled: true,
                perPage: pageLength,
                position: 'bottom',
                perPageDropdownEnabled: false,
                dropdownAllowAll: false
              }"
              :sort-options="{ enabled: true }"
              theme="polar-bear"
              mode="remote"
              compactMode
            >
              <template slot="table-row" slot-scope="props">
                <!-- Icon Column -->
                <span v-if="props.column.field === 'icon'">
                  <b-img
                    v-if="props.row.icon"
                    :src="getIconUrl(props.row.icon)"
                    alt="Subject Icon"
                    rounded
                    width="40"
                    height="40"
                  />
                  <b-avatar
                    v-else
                    variant="light-secondary"
                    size="40"
                  >
                    <feather-icon icon="BookIcon" size="20"/>
                  </b-avatar>
                </span>

                <!-- Name Column -->
                <span v-else-if="props.column.field === 'name'">
                  <div class="d-flex align-items-center">
                    <div>
                      <h6 class="mb-0">{{ props.row.name }}</h6>
                      <small v-if="props.row.description" class="text-muted">
                        {{ truncateText(props.row.description, 60) }}
                      </small>
                    </div>
                  </div>
                </span>

                <!-- Programs Count -->
                <span v-else-if="props.column.field === 'programs_count'">
                  <b-badge
                    :variant="props.row.programs && props.row.programs.length > 0 ? 'light-primary' : 'light-secondary'"
                    pill
                  >
                    {{ (props.row.programs && props.row.programs.length) || 0 }} Programs
                  </b-badge>
                </span>

                <!-- Status Column -->
                <span v-else-if="props.column.field === 'status'">
                  <b-badge
                    :variant="getStatusVariant(props.row.status)"
                    pill
                  >
                    {{ getStatusText(props.row.status) }}
                  </b-badge>
                </span>

                <!-- Created Date -->
                <span v-else-if="props.column.field === 'created_at'">
                  <small class="text-muted">{{ formatDate(props.row.created_at) }}</small>
                </span>

                <!-- Actions Column -->
                <span v-else-if="props.column.field === 'action'">
                  <div class="d-flex align-items-center">
                    <b-dropdown
                      variant="link"
                      no-caret
                      right
                    >
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="16"/>
                      </template>

                      <b-dropdown-item @click="editSubject(props.row.id)">
                        <feather-icon icon="EditIcon" size="16" class="mr-1"/>
                        <span>Edit</span>
                      </b-dropdown-item>

                      <b-dropdown-item @click="viewSubjectDetails(props.row)">
                        <feather-icon icon="EyeIcon" size="16" class="mr-1"/>
                        <span>View Details</span>
                      </b-dropdown-item>

                      <b-dropdown-divider></b-dropdown-divider>

                      <b-dropdown-item
                        @click="deleteSubject(props.row.id)"
                        variant="danger"
                      >
                        <feather-icon icon="TrashIcon" size="16" class="mr-1"/>
                        <span>Delete</span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </span>

                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>

              <!-- Empty state -->
              <template slot="emptystate">
                <div class="text-center py-4">
                  <feather-icon icon="BookIcon" size="48" class="text-muted mb-3"/>
                  <h5>No Subjects Found</h5>
                  <p class="text-muted">
                    {{ searchTerm ? 'No subjects match your search criteria.' : 'Start by creating your first subject.' }}
                  </p>
                  <b-button
                    v-if="!searchTerm"
                    variant="primary"
                    @click="resetForm"
                  >
                    <feather-icon icon="PlusIcon" size="16" class="mr-1"/>
                    Create Subject
                  </b-button>
                </div>
              </template>
            </vue-good-table>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>

import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormSelect,
  BPagination,
  BFormFile,
  BImg,
} from 'bootstrap-vue'
import {VueGoodTable} from 'vue-good-table'
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import {createNamespacedHelpers} from 'vuex';
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import formattedDate from '@/mixins/formattedDate'

const {mapState, mapActions} = createNamespacedHelpers('subjects')

export default {
  name: 'SubjectsManagement',
  components: {
    VueGoodTable,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormFile,
    BImg,
    BPagination,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    flatPickr,
  },
  mixins: [formattedDate],
  data() {
    return {
      pageLength: 10,
      dir: false,
      searchTerm: '',
      form: {
        name: '',
        description: '',
        icon: null,
      },
      activeSubject: {},

      // Enhanced columns for better table display
      enhancedColumns: [
        {
          label: 'Icon',
          field: 'icon',
          sortable: false,
          width: '80px',
        },
        {
          label: 'Subject',
          field: 'name',
          sortable: true,
          width: '300px',
        },
        {
          label: 'Programs',
          field: 'programs_count',
          sortable: false,
          width: '120px',
        },
        {
          label: 'Status',
          field: 'status',
          sortable: true,
          width: '100px',
        },
        {
          label: 'Created',
          field: 'created_at',
          sortable: true,
          width: '130px',
        },
        {
          label: 'Actions',
          field: 'action',
          sortable: false,
          width: '100px',
        },
      ],

      // Original columns for backward compatibility
      columns: [
        {
          label: 'Название',
          field: 'name',
        },
        {
          label: 'Иконка',
          field: 'icon',
        },
        {
          label: 'Описание',
          field: 'description',
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
    }
  },
  computed: {
    ...mapState({
      subjects: state => state.subjects
    }),

    subjectStats() {
      if (!Array.isArray(this.subjects)) return {
        total: 0,
        active: 0,
        withIcons: 0,
        totalPrograms: 0
      }

      return {
        total: this.subjects.length,
        active: this.subjects.filter(subject => subject.status !== 'D').length,
        withIcons: this.subjects.filter(subject => subject.icon).length,
        totalPrograms: this.subjects.reduce((sum, subject) => sum + (subject.programs?.length || 0), 0)
      }
    }
  },
  methods: {
    ...mapActions(['getSubjects', 'updateOrCreateSubject', 'deleteSubjectRecord']),
    createOrUpdateSubject() {
      let subjectData = new FormData();
      subjectData.append('name', this.form.name);
      subjectData.append('icon', this.form.icon);
      subjectData.append('description', this.form.description);
      let message = {
        title: 'Предмет был создан',
        message: 'Предмет создан'
      }
      if (this.form.id) {
        subjectData.append('id', this.form.id);
        message = {
          title: 'Предмет был отредактирован',
          message: 'Вы успешно отредактировали предмет'
        }
      }
      this.updateOrCreateSubject(subjectData)
          .then(() => {
            this.form = {
              name: '',
              description: '',
              icon: '',
            }
            this.$bvToast.toast(this.$t(message.title), {
              title: this.$t(message.message),
              variant: 'success',
              solid: true,
            })
          })
          .catch(err => {
            this.$bvToast.toast(this.$t('Ошибка сервера, попробуйте позже'), {
              title: this.$t('Ошибка сервера, обратитесь к администратору'),
              variant: 'danger',
              solid: true,
            })
          })

    },
    closeEditing() {
      this.activeTrial = {}
    },
    deleteSubject(id) {
      this.$bvModal
          .msgBoxConfirm('Пожалуйста, подтвердите, что вы хотите удалить этот предмет. Данный ' +
              'предмет может использоваться учителями в работе', {
            title: 'Подтверждение',
            size: 'sm',
            okVariant: 'danger',
            okTitle: 'Да',
            cancelTitle: 'Нет',
            cancelVariant: 'primary',
            hideHeaderClose: false,
            centered: true,
          })
          .then(res => {
            if (res) {
              this.deleteSubjectRecord(id)
                .then(() => {
                  this.$bvToast.toast(this.$t('Предмет был успешно удален'), {
                    title: this.$t('Вы удалили предмет'),
                    variant: 'warning',
                    solid: true,
                  })
                })
                  .catch(() => {
                    this.$bvToast.toast(this.$t('Ошибка сервера, попробуйте позже'), {
                      title: this.$t('Ошибка сервера, попробуйте позже или обратитесь к администратору'),
                      variant: 'danger',
                      solid: true,
                    })
                  })
            }
          })

    },
    editSubject(id) {
      let activeSubject = this.subjects.find(subject => subject.id === id);
      this.form = {
        name: activeSubject.name,
        description: activeSubject.description,
        icon: activeSubject.icon,
        id: activeSubject.id,
      }
    },
    removeEditing() {
      this.form = {
        name: '',
        description: '',
        icon: null,
      }
    },

    resetForm() {
      this.form = {
        name: '',
        description: '',
        icon: null,
      }
      // Reset validation
      this.$nextTick(() => {
        if (this.$refs.refFormObserver) {
          this.$refs.refFormObserver.reset()
        }
      })
    },

    // Enhanced helper methods for the new UI
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },

    getIconUrl(iconPath) {
      if (!iconPath) return null
      return `${process.env.VUE_APP_API_BASE_URL || ''}/storage/${iconPath}`
    },

    truncateText(text, length = 50) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },

    getStatusVariant(status) {
      switch (status) {
        case 'D':
          return 'light-danger'
        default:
          return 'light-success'
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'D':
          return 'Deleted'
        default:
          return 'Active'
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    },

    viewSubjectDetails(subject) {
      // Enhanced view details functionality
      this.$bvModal.msgBoxOk(`
        <h5>${subject.name}</h5>
        <p><strong>Description:</strong> ${subject.description || 'No description provided'}</p>
        <p><strong>Status:</strong> ${this.getStatusText(subject.status)}</p>
        <p><strong>Programs:</strong> ${subject.programs?.length || 0}</p>
        <p><strong>Created:</strong> ${this.formatDate(subject.created_at)}</p>
      `, {
        title: 'Subject Details',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'primary',
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        centered: true
      })
    }
  },
  created() {
    this.getSubjects()
  }
}
</script>

<style lang="scss" scoped>
.subjects-management {
  .earnings-card {
    border: none;
    box-shadow: 0 4px 25px 0 rgba(0,0,0,0.1);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 30px 0 rgba(0,0,0,0.15);
    }
  }

  .quick-actions-card {
    border: none;
    box-shadow: 0 4px 25px 0 rgba(0,0,0,0.1);
    background: linear-gradient(118deg, rgba(115, 103, 240, 0.05), rgba(255, 255, 255, 0.95));
    border-left: 4px solid #7367F0;

    h5 {
      color: #2c2c2c;
      font-weight: 600;
      display: flex;
      align-items: center;

      svg {
        color: #7367F0;
      }
    }
  }

  .card {
    border: none;
    box-shadow: 0 4px 25px 0 rgba(0,0,0,0.1);
  }

  .custom-data-bs-table {
    .per-page-selector {
      width: 70px;
    }
  }

  .breadcrumb {
    background: transparent;
    margin-bottom: 0;

    .breadcrumb-item {
      font-size: 0.857rem;

      &.active {
        color: #6c757d;
      }

      a {
        color: #7367F0;
        text-decoration: none;

        &:hover {
          color: #5e50ee;
        }
      }
    }
  }

  .content-header-title {
    color: #2c2c2c;
    font-weight: 600;
    display: flex;
    align-items: center;

    svg {
      color: #7367F0;
    }
  }
}

// Global table styling
::v-deep .vgt-table {
  border: none;

  th {
    background: #f8f9fa !important;
    border: none;
    font-weight: 600;
    color: #2c2c2c;
    font-size: 0.857rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    border: none;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
    padding: 1rem 0.714rem;
  }

  .vgt-responsive {
    border: none;
  }

  tbody tr {
    transition: all 0.3s ease;

    &:hover {
      background: rgba(115, 103, 240, 0.05);
      transform: translateY(-1px);
    }
  }
}

::v-deep .vgt-pagination {
  .vgt-pagination__info {
    color: #6c757d;
    font-size: 0.857rem;
  }

  .vgt-pagination__page-info {
    .vgt-pagination__page-info__current-entry {
      font-weight: 600;
      color: #7367F0;
    }
  }

  .pagination {
    .page-item {
      &.active .page-link {
        background-color: #7367F0;
        border-color: #7367F0;
      }

      .page-link {
        color: #6c757d;
        border-color: #dee2e6;

        &:hover {
          background-color: rgba(115, 103, 240, 0.1);
          border-color: #7367F0;
          color: #7367F0;
        }
      }
    }
  }
}

// Form enhancements
::v-deep .form-control {
  border-radius: 0.375rem;
  border: 1px solid #d8d6de;

  &:focus {
    border-color: #7367F0;
    box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
  }
}

::v-deep .btn-primary {
  background-color: #7367F0;
  border-color: #7367F0;

  &:hover {
    background-color: #5e50ee;
    border-color: #5e50ee;
  }
}

::v-deep .btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;

  &:hover {
    background-color: #6c757d;
    border-color: #6c757d;
  }
}
</style>
